import { Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useRef } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { formatDatetimeForPreview } from "src/utils/date-utils";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { enqueueSnackbar } from "notistack";
import { useAuthContext } from "src/auth/hooks";
import { DownloadExcelButton, ReportRunner, useDaysDormantFilter, useLandmarkFilter } from "../runner/report-runner";

type DormantAssetsLine = {
	deviceId: number,
  location: string,
	lastPosLat: string,
	lastPosLong: string,
	lastPing?: string,
	deviceType: string,
	dormantForDays: number,
}

type DormantAssetsReportLinesProps = {
  lines: DormantAssetsLine[],
}

const DormantAssetsReportLines = memo(({ lines }: DormantAssetsReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines);

  const { session } = useAuthContext();
  if (!sortedList) {
    return null;
  }

  const timezone = session?.organization?.companyTimezone as string;

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="deviceType">
                Asset
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="lastPosLat">
                Last Location
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="lastPosLat">
                Last Position
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="lastPing" align="center">
                Last Ping
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="dormantForDays" align="right">
                Dormant For Days
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {line.deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {line.location}
                </TruxTableCell>
                <TruxTableCell>
                  {line.lastPosLat}, {line.lastPosLong}
                </TruxTableCell>
                <TruxTableCell align="center">
                  {formatDatetimeForPreview(line.lastPing, timezone)}
                </TruxTableCell>
                <TruxTableCell align="right">
                  {line.dormantForDays === 7 ? "7+" : line.dormantForDays}
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  lines?: DormantAssetsLine[],
}

export default function Page() {
  const settings = useSettingsContext();

  const router = useRouter();

  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => ({
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.dormantAssets.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data.lines),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  // const dateRangeFilter = useDateRangeFilter();
  // const organizationFilter = useOrganizationFilter();
  const daysDormantFilter = useDaysDormantFilter();
  const landmarkFilter = useLandmarkFilter();

  const filters = [
    // dateRangeFilter,
    // organizationFilter,
    daysDormantFilter,
    landmarkFilter
  ];


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Dormant Assets">
          <DownloadExcelButton baseUrl={endpoints.reports.dormantAssets.excel} filters={filters} currentRequest={currentRequest} />
        </PageHeader>

        <ReportRunner
          filters={filters}
          onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
          onRun={(request) => {
            currentRequest.current = request;
            setShouldRefresh(true);
          }}
          loading={loading}
        />

        {data && (data.lines?.length || 0) > 0 && <DormantAssetsReportLines lines={data.lines || []} />}
        {loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container >
  );
}
